/**
 * Subscription Tier Configuration — Single Source of Truth
 *
 * Every frontend component that renders tier-specific information
 * (PaywallModal, SubscriptionDrawer, UpgradeComparisonCard, etc.)
 * MUST read from this file instead of hard-coding tier details.
 *
 * Backend enforcement limits live in `tier_feature_config` DB table;
 * the numbers here mirror them for display purposes only.
 *
 * Tier naming: basic = CC Essentials, full = CC Chat+, extended = CC Pro
 * See SUBSCRIPTION_TIERS_V2_ESSENTIALS_MARCH_2026.md §10.1
 *
 * Mission 33 — Subscription System
 * Created: February 2026
 */

import type { SubscriptionTier, SubscriptionPeriod } from '@/services/subscriptionService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TierPricing {
  /** Monthly price in USD — the store's list price, the same as the backend's
   *  services/tier_pricing.py (tests/gate/test_app_tier_prices_match_backend.py). */
  monthly: number;
  /** Annual price in USD (0 = not available) — pinned the same way. */
  annual: number;
  /** Effective monthly cost when billed annually (annual / 12) */
  annualMonthly: number;
  /** Percentage saved when choosing annual vs monthly */
  annualSavingsPercent: number;
}

/** Build a TierPricing object — derived fields calculated automatically from monthly + annual. */
function buildPricing(monthly: number, annual: number): TierPricing {
  return {
    monthly,
    annual,
    annualMonthly: annual > 0 ? parseFloat((annual / 12).toFixed(2)) : 0,
    annualSavingsPercent: annual > 0 ? Math.round((1 - annual / (monthly * 12)) * 100) : 0,
  };
}

/**
 * Store product identifiers keyed by platform, then by billing period.
 *
 * iOS and Android use DIFFERENT identifier formats:
 *   - iOS (App Store Connect): reverse-DNS bundle, e.g. `com.coachcedar.app.basic.monthly`
 *   - Android (Google Play): compound `{productId}:{basePlanId}`, e.g. `basic_monthly:coach-cedar-basic-monthly`
 *
 * `null` means the product does not exist on that platform (or is non-purchasable,
 * like `com.coachcedar.app.basic.annual` which is "Developer Removed from Sale").
 * Callers MUST handle null — never attempt to purchase a product with a null ID.
 */
export interface PlatformProductIds {
  ios: Record<SubscriptionPeriod, string | null>;
  android: Record<SubscriptionPeriod, string | null>;
}

export interface TierConfig {
  /** Unique tier key — matches backend `subscription_tier` values */
  id: SubscriptionTier;

  /** Display name shown in UI (e.g. "CC Essentials", "CC Chat+") */
  displayName: string;

  /** Short label for badges and compact views */
  shortLabel: string;

  /** RevenueCat entitlement identifier */
  entitlementId: string;

  /** Store product IDs by platform + billing period. See PlatformProductIds docstring. */
  productIds: PlatformProductIds;

  /**
   * 🌞 Summer50 — no-trial store product IDs (charge immediately, no 7-day intro).
   * Used for a returning user whose free month has ended and who never subscribed:
   * Apple/Google won't grant a second intro trial, so a distinct `.notrial` product
   * is required to convert them. `undefined`/null on any platform+period means the
   * `.notrial` variant is not configured yet → callers gracefully fall back to the
   * standard `productIds` (which may carry an intro trial). Paul's ASC/Play products
   * land by wave-1 + 30d; drop the real identifiers in here when they exist.
   */
  noTrialProductIds?: PlatformProductIds;

  /** Pricing (USD). Trial has zeroes since it's free. */
  pricing: TierPricing;

  /** Weekly ad-hoc chat message limit (display only — enforcement is backend) */
  weeklyMessageLimit: number;

  /** Weekly debrief limit */
  weeklyDebriefLimit: number;

  /**
   * Features unique to this tier (shown in comparison cards).
   * These are IN ADDITION to universalFeatures.
   */
  differentiators: string[];

  /** LLM model used for chat */
  chatModel: string;

  /** Whether this tier is the default offering for the trial CTA */
  isTrialDefault: boolean;

  /** Sort order for display (lower = shown first) */
  sortOrder: number;

  /** Whether users on this tier can upgrade (shows upgrade path) */
  canUpgrade: boolean;

  /** The tier to upgrade TO (null if top tier or trial) */
  upgradeTo: SubscriptionTier | null;
}

// ---------------------------------------------------------------------------
// Universal features — shared across ALL paid tiers (and trial)
// These are the core coaching features every subscriber gets.
// §2.2: Watch sync IN (receive data) is universal; push OUT is Chat+ only.
// ---------------------------------------------------------------------------

export const UNIVERSAL_FEATURES: string[] = [
  'Custom training plan rebuilt every week based on your goals, fitness, and schedule',
  'Post-workout debriefs after every session',
  'Daily morning check-in with Coach Cedar',
  'Weekly Coach Cedar Report with progress insights',
  'Weekly check-in to review your report with Coach Cedar',
  'On-demand plan changes via recalibration',
  'Research-backed coaching intelligence across athletics, nutrition, sleep, and more',
  'Health & fitness device sync (Apple Watch, Garmin, Samsung, Strava, Peloton)',
  'Goal setting with support for multiple goals at once',
];

// ---------------------------------------------------------------------------
// Essentials feature bullets — used in PaywallModal for initial paywall (§7.3 Block B)
// This is what the user sees when subscribing to CC Essentials.
// Pass the health platform label for platform-specific text.
// ---------------------------------------------------------------------------

export function getPlanFeatureBullets(_healthPlatformLabel: string): { icon: string; heading: string; description: string }[] {
  return [
    {
      icon: '\u2726',
      heading: 'Your coach, always with you',
      description: 'Knows your history, adapts to your life, and adjusts whenever you need it.',
    },
    {
      icon: '\u2726',
      heading: 'A plan built around you',
      description: 'Plans shaped to your goals, injuries, schedule, and ambitions \u2014 from casual to competitive. Informed by 1,000+ peer reviewed research sources and proven frameworks.',
    },
    {
      icon: '\u2726',
      heading: 'Daily check-ins and on-demand changes',
      description: 'Morning touch-points, post-workout debriefs, and instant plan adjustments whenever you ask.',
    },
    {
      icon: '\u2726',
      heading: 'Weekly report and check-in',
      description: 'Training insights and a coaching conversation to review your week, the next week\u2019s plan, and work through any issues coming up for you.',
    },
    {
      icon: '\u2726',
      heading: 'Data-informed coaching',
      description: 'Smartwatch sync, phone health data integration, and collaborates with other fitness apps, coaches, and programs.',
    },
  ];
}

// ---------------------------------------------------------------------------
// Upgrade driver bullets — shown to Essentials subscribers (§7.3 Block C)
// These are the 3 features gated to Chat+ that drive upgrades.
// ---------------------------------------------------------------------------

export const UPGRADE_DRIVER_BULLETS: { icon: string; title: string; description: string }[] = [
  {
    icon: '\uD83D\uDCAC', // 💬
    title: 'Coach Cedar whenever you need it',
    description: 'Your morning connection, debrief, and weekly check-in are built into Essentials. Chat+ adds the ability to reach Coach Cedar anytime in between \u2014 not just at your regular connection times. Get 30 extra chat exchanges per week.',
  },
  {
    icon: '\uD83D\uDCF7', // 📷
    title: 'Share photos and documents',
    description: 'Medical records, physio notes, progress photos, form shots. Coach Cedar reads them, remembers them, and coaches from them.',
  },
  {
    icon: '\u231A', // ⌚
    title: 'Push workouts to your watch',
    description: 'Send your session directly to your Apple Watch or Garmin before you head out.',
  },
];

// ---------------------------------------------------------------------------
// Tier definitions
// ---------------------------------------------------------------------------

export const TIER_CONFIG: TierConfig[] = [
  // ----- Trial -----
  // §1.1: Trial subscribes to basic_monthly but gets Chat+ level access for 7 days.
  // The trial is the free period of the Essentials subscription.
  {
    id: 'trial',
    displayName: 'Free Trial',
    shortLabel: 'Trial',
    entitlementId: 'basic_plan', // Trial is the free period of Essentials subscription
    productIds: {
      ios: {
        monthly: 'com.coachcedar.app.basic.monthly',
        annual: null,
      },
      android: {
        monthly: 'basic_monthly:coach-cedar-basic-monthly',
        annual: null,
      },
    },
    pricing: buildPricing(0, 0),
    weeklyMessageLimit: 0, // Unlimited during 7-day trial (0 = no enforced limit)
    weeklyDebriefLimit: 5,
    differentiators: [
      'Full Chat+ level access for 7 days',
      'Open chat, uploads, watch push — experience the full Coach Cedar',
    ],
    chatModel: 'gpt-5.1',
    isTrialDefault: true,
    sortOrder: 0,
    canUpgrade: false, // Trial rolls into Essentials automatically
    upgradeTo: null,
  },

  // ----- Basic (CC Essentials) -----
  // §2.1-2.3: Planning-centric tier. Structured touchpoints, no ad-hoc chat.
  {
    id: 'basic',
    displayName: 'CC Essentials',
    shortLabel: 'Essentials',
    entitlementId: 'basic_plan',
    productIds: {
      ios: {
        monthly: 'com.coachcedar.app.basic.monthly',
        // App Store Connect has `com.coachcedar.app.basic.annual` but it's
        // "Developer Removed from Sale" — non-purchasable, so treat as null.
        annual: null,
      },
      android: {
        monthly: 'basic_monthly:coach-cedar-basic-monthly',
        // Play Console has no `basic_annual` product — Essentials is monthly-only.
        annual: null,
      },
    },
    // 🌞 Summer50 — `.notrial` twin (same price, NO intro trial) served to the
    // ended-free-month cohort. iOS IDs follow the `<original>.notrial` convention
    // created in ASC per SUMMER50_OPS_RUNBOOK.md §2 SKU C; the paywall falls back
    // to the standard product until the twin exists in RC. Android needs no twins
    // (Play trials are offers on the base plan — the app just withholds the token).
    noTrialProductIds: {
      ios: { monthly: 'com.coachcedar.app.basic.monthly.notrial', annual: null },
      android: { monthly: null, annual: null },
    },
    pricing: buildPricing(12.99, 0), // Annual not offered for Essentials
    weeklyMessageLimit: 0, // No ad-hoc chat
    weeklyDebriefLimit: 5,
    differentiators: [
      'Customize Coach Cedar to how you like to be coached',
      'Custom plan, adapted every week',
      'Get your plan right \u2014 Ask Coach Cedar to make changes anytime',
      'Morning touchpoint with Coach Cedar every day',
      'Post-workout debrief after every session',
      'Get a Coach Cedar Report with progress insights',
      'Full coaching check-in every Sunday',
      'Connect to your phone\u2019s health data',
      'Informed by 1,000+ peer-reviewed research sources',
      'Remembers history, injuries, preferences, progress \u2014 adapts as your life changes',
    ],
    chatModel: 'gpt-5.1',
    isTrialDefault: false,
    sortOrder: 1,
    canUpgrade: true,
    upgradeTo: 'full',
  },

  // ----- Full (CC Chat+) -----
  {
    id: 'full',
    displayName: 'CC Chat+',
    shortLabel: 'Chat+',
    entitlementId: 'full_plan',
    productIds: {
      ios: {
        monthly: 'com.coachcedar.app.full.monthly',
        annual: 'com.coachcedar.app.full.annual',
      },
      android: {
        // `full_monthly:full-monthly-base` format confirmed via live webhook in
        // revenuecat_webhook_events (Apr 3 2026 renewal).
        monthly: 'full_monthly:full-monthly-base',
        annual: 'full_annual:full-annual-base',
      },
    },
    // 🌞 Summer50 `.notrial` twins — see the Essentials entry for the convention.
    noTrialProductIds: {
      ios: { monthly: 'com.coachcedar.app.full.monthly.notrial', annual: 'com.coachcedar.app.full.annual.notrial' },
      android: { monthly: null, annual: null },
    },
    // $224.99/yr: the App Store listing's price (checked 2026-09-26) and the documented one,
    // 25% off monthly. Was 249.99 ("Save 17%"). Pinned to the backend's
    // services/tier_pricing.py by tests/gate/test_app_tier_prices_match_backend.py.
    pricing: buildPricing(24.99, 224.99),
    weeklyMessageLimit: 30,
    weeklyDebriefLimit: 5,
    differentiators: [
      'Everything in CC Essentials, plus Coach Cedar whenever you need it \u2014 not just at your regular connection times',
      '30 extra chat exchanges with Coach Cedar',
      'Share photos, documents, and push workouts straight to your watch',
    ],
    chatModel: 'gpt-5.1',
    isTrialDefault: false,
    sortOrder: 2,
    canUpgrade: true,
    upgradeTo: 'extended',
  },

  // ----- Extended (CC Pro) -----
  {
    id: 'extended',
    displayName: 'CC Pro',
    shortLabel: 'Pro',
    entitlementId: 'extended_plan',
    productIds: {
      ios: {
        monthly: 'com.coachcedar.app.extended.monthly',
        annual: 'com.coachcedar.app.extended.annual',
      },
      android: {
        monthly: 'extended_monthly:coach-cedar-extended-monthly',
        annual: 'extended_annual:coach-cedar-extended-annual',
      },
    },
    // 🌞 Summer50 `.notrial` twins — see the Essentials entry for the convention.
    noTrialProductIds: {
      ios: { monthly: 'com.coachcedar.app.extended.monthly.notrial', annual: 'com.coachcedar.app.extended.annual.notrial' },
      android: { monthly: null, annual: null },
    },
    pricing: buildPricing(39.99, 319.99),
    weeklyMessageLimit: 0, // Unlimited
    weeklyDebriefLimit: 5,
    differentiators: [
      'Everything in CC Chat+ with no chat limits',
      'Coach Cedar is always on, whenever you want, for as long as you need',
    ],
    chatModel: 'gpt-5.1',
    isTrialDefault: false,
    sortOrder: 3,
    canUpgrade: false,
    upgradeTo: null,
  },
];

// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------

/** Get config for a specific tier */
export function getTierConfig(tier: SubscriptionTier): TierConfig {
  const config = TIER_CONFIG.find((t) => t.id === tier);
  if (!config) {
    throw new Error(`Unknown subscription tier: ${tier}`);
  }
  return config;
}

/** Get the tier a user should upgrade TO (null if already top tier or trial) */
export function getUpgradeTier(currentTier: SubscriptionTier): TierConfig | null {
  const current = getTierConfig(currentTier);
  if (!current.canUpgrade || !current.upgradeTo) return null;
  return getTierConfig(current.upgradeTo);
}

/** Get all paid tiers sorted by sortOrder */
export function getPaidTiers(): TierConfig[] {
  return TIER_CONFIG.filter((t) => t.id !== 'trial').sort((a, b) => a.sortOrder - b.sortOrder);
}

/** Get the default trial tier config */
export function getTrialTier(): TierConfig {
  return getTierConfig('trial');
}

/** Get the Essentials tier config (used for paywall) */
export function getEssentialsTier(): TierConfig {
  return getTierConfig('basic');
}

/** Format price for display (e.g. "$24.99/mo") */
export function formatPrice(amount: number, period: SubscriptionPeriod): string {
  if (amount === 0) return 'Free';
  return `$${amount.toFixed(2)}/${period === 'monthly' ? 'mo' : 'yr'}`;
}

/** Get annual savings text (e.g. "Save 25%") */
export function getAnnualSavingsText(tier: SubscriptionTier): string | null {
  const config = getTierConfig(tier);
  if (config.pricing.annualSavingsPercent <= 0) return null;
  return `Save ${config.pricing.annualSavingsPercent}%`;
}

/**
 * Resolve the store product identifier for a tier + billing period on the
 * given platform. Pass `Platform.OS` at call sites.
 *
 * Returns null when:
 *   - the product does not exist on that platform (e.g. Essentials annual),
 *   - the platform does not support IAP (web, unknown),
 *   - the tier is unknown.
 *
 * Callers MUST handle null — never pass null through to Purchases.find()/purchase().
 */
export function getProductIdForPlatform(
  tierId: SubscriptionTier,
  period: SubscriptionPeriod,
  platform: string,
): string | null {
  if (platform !== 'ios' && platform !== 'android') return null;
  const tier = TIER_CONFIG.find((t) => t.id === tierId);
  if (!tier) return null;
  return tier.productIds[platform][period] ?? null;
}

// ---------------------------------------------------------------------------
// 🌞 Summer50 — no-trial product resolution
// ---------------------------------------------------------------------------

/** RevenueCat offering identifier that holds the no-intro-trial packages.
 * Paul configures this offering server-side; the FE just selects the matching
 * product from the flattened package list (getOfferings collects all offerings). */
export const NO_TRIAL_OFFERING_ID = 'no_trial';

/**
 * No-trial store product id for a tier + period, or null when the `.notrial`
 * variant isn't configured for that platform yet (the graceful-empty state).
 */
export function getNoTrialProductIdForPlatform(
  tierId: SubscriptionTier,
  period: SubscriptionPeriod,
  platform: string,
): string | null {
  if (platform !== 'ios' && platform !== 'android') return null;
  const tier = TIER_CONFIG.find((t) => t.id === tierId);
  if (!tier?.noTrialProductIds) return null;
  return tier.noTrialProductIds[platform][period] ?? null;
}

/**
 * Resolve which store product to purchase. When `preferNoTrial` is true (a
 * returning free-month-ended user who never subscribed), prefer the `.notrial`
 * product; if it isn't configured yet, fall back to the standard product so the
 * paywall still works (interim: the user may see a trial until Paul's `.notrial`
 * products go live). Returns null only when even the standard product is absent.
 */
export function resolvePurchaseProductId(
  tierId: SubscriptionTier,
  period: SubscriptionPeriod,
  platform: string,
  preferNoTrial: boolean,
): string | null {
  if (preferNoTrial) {
    const noTrial = getNoTrialProductIdForPlatform(tierId, period, platform);
    if (noTrial) return noTrial;
  }
  return getProductIdForPlatform(tierId, period, platform);
}
